import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  styled,
  Tabs,
  Tab,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import { getApiData, postFormData } from "../../utils/APIHelper";
import styles from "./styles";
import budgetStyles from "../NewProjectDetails/components/Budgets/styles";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import AddItemModal from "../NewProjectDetails/components/Budgets/AddItemModal";
import DeleteBoqModal from "../NewProjectDetails/components/Budgets/DeleteBoqModal";
import DeleteInclusionModal from "../NewProjectDetails/components/Budgets/DeleteInclusionModal";
import { addBOQToInclusion, deleteBOQ, updateBOQ, deleteInclusion } from "../../services/budgetService";
import { getRoomStyles, getRoomPalettes, updateRoom } from "../../services/roomService";
import { RoomCard } from "../../components/ProjectLevelBOQ";
import RoomActivities from "./RoomActivities";
import RoomScope from "./RoomScope";

const paletteColors = {
  "Warm greys": ["#F2F1EF", "#D8D4CF", "#B8B2AC", "#8F8A84", "#5F5A54"],
  "Neutral tones": ["#F5F5F5", "#E0E0E0", "#C2C2C2", "#9E9E9E", "#616161"],
  "Jewel tones": ["#2C3E50", "#6A1B9A", "#0B5345", "#1F618D", "#7D3C98"],
  "Coastal blue": ["#EAF6F6", "#A8DADC", "#5DADE2", "#2874A6", "#1B4F72"],
  "Forest green": ["#E8F5E9", "#A5D6A7", "#388E3C", "#1B5E20", "#0B3D0B"],
  "Earth and terracotta": ["#F4E1D2", "#D7A86E", "#C65D3A", "#9C3D2E", "#5C2A1D"],
};

const ColorPalettePreview = ({ colors }) => {
  if (!colors || colors.length === 0) return null;
  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex" }}>
      {colors.map((color, index) => (
        <Box key={index} sx={{ flex: 1, backgroundColor: color }} />
      ))}
    </Box>
  );
};


const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d !important" : "#fff !important",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    cursor: "not-allowed !important",
    backgroundColor: "#f5f5f5 !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
}));

const RoomDetails = () => {
  const { id, roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [activeTab, setActiveTab] = useState(0);
  const { userData } = useSelector((state) => state.auth);
  const hideUnitPrice = !!userData?.contractor_data;

  const roomStyles = styles(isMobile);

  // Get room data from navigation state
  const { roomData, projectDetails, passedStyles } = location.state || {};
  const details = projectDetails || {};
  const currentRoomData = roomData || {};

  const [inclusions, setInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInclusions, setExpandedInclusions] = useState({});
  const [addingInclusion, setAddingInclusion] = useState(null);
  const [availableInclusions, setAvailableInclusions] = useState([]);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [selectedInclusionForItem, setSelectedInclusionForItem] =
    useState(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingBoq, setEditingBoq] = useState(null);
  const [isDeleteBoqModalOpen, setIsDeleteBoqModalOpen] = useState(false);
  const [boqToDelete, setBoqToDelete] = useState(null);
  const [isDeleteInclusionModalOpen, setIsDeleteInclusionModalOpen] = useState(false);
  const [inclusionToDelete, setInclusionToDelete] = useState(null);

  // Image upload states
  const [uploadingImages, setUploadingImages] = useState(false);
  const [images, setImages] = useState({
    inspirations: [],
    floorplans_as_built: [],
    floorplans_target: [],
    renders_3d: [],
    elevations: [],
    pictures: [],
  });

  // Style selection states
  const [availableStyles, setAvailableStyles] = useState([]);
  const [loadingStyles, setLoadingStyles] = useState(false);
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [isUpdatingStyle, setIsUpdatingStyle] = useState(false);
  const [currentStyle, setCurrentStyle] = useState(null);

  // Palette selection states
  const [availablePalettes, setAvailablePalettes] = useState([]);
  const [loadingPalettes, setLoadingPalettes] = useState(false);
  const [isPaletteModalOpen, setIsPaletteModalOpen] = useState(false);
  const [selectedPaletteId, setSelectedPaletteId] = useState(null);
  const [isUpdatingPalette, setIsUpdatingPalette] = useState(false);
  const [currentPalette, setCurrentPalette] = useState(null);

  // Display state for room style/palette names (kept in sync after edits)
  const [displayStyleName, setDisplayStyleName] = useState(
    currentRoomData?.style || ""
  );
  const [displayPaletteName, setDisplayPaletteName] = useState(
    currentRoomData?.palette || ""
  );

  // Calculate room total from inclusions
  const roomTotal = useMemo(() => {
    if (!inclusions) return 0;
    return inclusions.reduce(
      (total, inclusion) => total + (inclusion.inclusion_amount || 0),
      0
    );
  }, [inclusions]);

  // Get list of already added inclusion names
  const addedInclusionNames = useMemo(() => {
    if (!inclusions) return [];
    return inclusions.map((inclusion) => inclusion.name);
  }, [inclusions]);

  // Fetch images for different tabs
  const fetchImages = async (type, tags) => {
    try {
      const response = await getApiData(
        `api/project/files/${roomId}?type=${type}&tags=${tags}`,
        "GET",
        {}
      );
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching ${tags} images:`, error);
      return [];
    }
  };

  // Load images for all tabs
  const loadAllImages = async () => {
    if (!roomId) return;

    try {
      const [
        inspirations,
        floorplansAsBuilt,
        floorplansTarget,
        renders3d,
        elevations,
        pictures,
      ] = await Promise.all([
        fetchImages("room", "INSPIRATION"),
        fetchImages("room", "FLOORPLAN_AS_BUILT"),
        fetchImages("room", "FLOORPLAN_TARGET"),
        fetchImages("room", "3D_RENDER"),
        fetchImages("room", "ELEVATION"),
        fetchImages("room", "PICTURE"),
      ]);

      setImages({
        inspirations,
        floorplans_as_built: floorplansAsBuilt,
        floorplans_target: floorplansTarget,
        renders_3d: renders3d,
        elevations,
        pictures,
      });
    } catch (error) {
      console.error("Error loading images:", error);
    }
  };

  // Fetch room styles for current room name
  const fetchRoomStyles = async (roomName) => {
    if (!roomName) return;
    setLoadingStyles(true);
    try {
      const response = await getRoomStyles(roomName);
      if (response?.success && response.data) {
        setAvailableStyles(response.data);
        const current = response.data.find(
          (style) => style.name === currentRoomData?.style
        );
        if (current) {
          setCurrentStyle(current);
          setSelectedStyleId(current.id);
        }
      } else {
        setAvailableStyles([]);
      }
    } catch (error) {
      console.error("Error fetching room styles:", error);
      setAvailableStyles([]);
    } finally {
      setLoadingStyles(false);
    }
  };

  // Fetch room palettes
  const fetchRoomPalettes = async () => {
    setLoadingPalettes(true);
    try {
      const response = await getRoomPalettes();
      if (response?.success && response.data) {
        setAvailablePalettes(response.data);
        const current = response.data.find(
          (palette) => palette.name === currentRoomData?.palette
        );
        if (current) {
          setCurrentPalette(current);
          setSelectedPaletteId(current.id);
        }
      } else {
        setAvailablePalettes([]);
      }
    } catch (error) {
      console.error("Error fetching room palettes:", error);
      setAvailablePalettes([]);
    } finally {
      setLoadingPalettes(false);
    }
  };

  useEffect(() => {
    if (id && roomId) {
      loadRoomData();
      loadAllImages();
    } else {
      // If no id or roomId, stop loading
      setLoading(false);
    }
  }, [id, roomId]);

  useEffect(() => {
    if (currentRoomData?.room_name) {
      fetchRoomStyles(currentRoomData.room_name);
    }
    fetchRoomPalettes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomData?.room_name]);

  const loadRoomData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Loading room data for project:", id, "room:", roomId);

      // API Call 1: Get room inclusions
      const inclusionsResponse = await getApiData(
        `api/project/${id}/room/${roomId}/inclusions`,
        "GET",
        {}
      );

      if (inclusionsResponse.success) {
        setInclusions(inclusionsResponse.inclusions || []);
        console.log("Found room inclusions:", inclusionsResponse.inclusions);
      } else {
        console.log("No inclusions found for room:", roomId);
        setInclusions([]);
      }

      // API Call 2: Get available inclusions for project type
      if (details?.project_type) {
        try {
          const availableResponse = await getApiData(
            `api/rooms/inclusions?project_type=${details.project_type}`,
            "GET",
            {}
          );

          if (availableResponse.success) {
            setAvailableInclusions(availableResponse.inclusions || []);
            console.log("Available inclusions:", availableResponse.inclusions);
          }
        } catch (availableError) {
          console.log("Could not load available inclusions:", availableError);
          setAvailableInclusions([]);
        }
      }
    } catch (error) {
      console.error("Error loading room data:", error);
      setError(error);
      toast.error("Failed to load room data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/project/details/${id}/rooms`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleInclusion = (inclusionId) => {
    setExpandedInclusions((prev) => ({
      ...prev,
      [inclusionId]: !prev[inclusionId],
    }));
  };

  const handleAddInclusion = async (inclusionName) => {
    setAddingInclusion(inclusionName);
    try {
      const response = await getApiData(
        `api/project/${id}/room/${roomId}/inclusion`,
        "POST",
        { name: inclusionName }
      );

      if (response.success) {
        toast.success(`Successfully added ${inclusionName} to room`);
        loadRoomData(); // Reload data
      } else {
        toast.error("Failed to add inclusion");
      }
    } catch (error) {
      console.error("Error adding inclusion:", error);
      toast.error("Failed to add inclusion");
    } finally {
      setAddingInclusion(null);
    }
  };

  const handleOpenAddItemModal = (inclusion) => {
    setSelectedInclusionForItem(inclusion);
    setIsEditingItem(false);
    setEditingBoq(null);
    setIsAddItemModalOpen(true);
  };

  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
    setSelectedInclusionForItem(null);
    setIsEditingItem(false);
    setEditingBoq(null);
  };

  const handleEditBoq = (boq, inclusion) => {
    console.log("Editing BOQ item:", boq, "in inclusion:", inclusion);
    setEditingBoq(boq);
    setSelectedInclusionForItem(inclusion);
    setIsEditingItem(true);
    setIsAddItemModalOpen(true);
  };

  const handleDeleteBoq = (boq, inclusionId) => {
    setBoqToDelete({ ...boq, inclusionId });
    setIsDeleteBoqModalOpen(true);
  };



  const handleConfirmDeleteBoq = async (boqId, inclusionId) => {
    try {
      const response = await deleteBOQ(id, boqId);
      if (response.success) {
        toast.success("BOQ item deleted successfully");
        loadRoomData();
        setIsDeleteBoqModalOpen(false);
        setBoqToDelete(null);
      } else {
        toast.error("Failed to delete BOQ item");
      }
    } catch (error) {
      console.error("Error deleting BOQ:", error);
      toast.error("Failed to delete BOQ item");
    }
  };

  const handleCloseDeleteBoqModal = () => {
    setIsDeleteBoqModalOpen(false);
    setBoqToDelete(null);
  };

  const handleDeleteInclusion = (inclusion) => {
    setInclusionToDelete(inclusion);
    setIsDeleteInclusionModalOpen(true);
  };

  const handleConfirmDeleteInclusion = async (inclusionId) => {
    try {
      const response = await deleteInclusion(inclusionId);
      if (response.success) {
        toast.success("Inclusion deleted successfully");
        loadRoomData();
        setIsDeleteInclusionModalOpen(false);
        setInclusionToDelete(null);
      } else {
        toast.error("Failed to delete inclusion");
      }
    } catch (error) {
      console.error("Error deleting inclusion:", error);
      toast.error("Failed to delete inclusion");
    }
  };

  const handleCloseDeleteInclusionModal = () => {
    setIsDeleteInclusionModalOpen(false);
    setInclusionToDelete(null);
  };

  const handleUpdateBoq = async (itemData) => {
    if (!editingBoq || !selectedInclusionForItem) return;
    console.log("Updating BOQ item:", editingBoq, "with data:", itemData);

    setIsAddingItem(true);
    try {
      const updateData = {
        name: itemData.item_name,
        boq_type: itemData.boq_type,
        subcategory: itemData.subcategory,
        supply_type: itemData.supply_apply,
        specification: itemData.specification,
        material_unit: itemData.material_unit,
        qty: itemData.boq_type === "Materials" ? itemData.quantity : null,
        days: itemData.boq_type === "Activities" ? itemData.days : null,
        // AddItemModal already coerces lead_time → int|null based on the
        // current supply_apply selection, so forward as-is.
        lead_time: itemData.lead_time != null ? itemData.lead_time : null,
      };

      const response = await updateBOQ(id, editingBoq.id, updateData);

      if (response.success) {
        toast.success("BOQ item updated successfully");
        loadRoomData();
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to update BOQ item");
      }
    } catch (error) {
      console.error("Error updating BOQ:", error);
      toast.error("Failed to update BOQ item");
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleDeleteBoqImage = async (imageId) => {
    try {
      const response = await getApiData(
        `api/project/delete-files/${imageId}`,
        "POST",
        {}
      );
      if (response.success) {
        toast.success("Image deleted successfully");
        // Update the editingBoq state to remove the image
        setEditingBoq((prev) => ({
          ...prev,
          image: [],
        }));
        // Reload room data to refresh the image
        loadRoomData();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting BOQ image:", error);
      toast.error("Failed to delete image");
    }
  };

  const handleUploadBoqImage = async (file) => {
    if (!editingBoq) return;

    try {
      const formData = new FormData();
      formData.append("project_id", editingBoq.id);
      formData.append("type", "budgetItemDetails");
      formData.append("file_1", file);

      const response = await postFormData("api/project/files", formData);

      if (response.success) {
        toast.success("Image uploaded successfully");
        // Update the editingBoq state with new image
        if (response.data && response.data.length > 0) {
          setEditingBoq((prev) => ({
            ...prev,
            image: [{ image: response.data[0].path, id: response.data[0].id }],
          }));
        }
        // Reload room data to refresh the image
        loadRoomData();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading BOQ image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleAddItem = async (itemData) => {
    if (!selectedInclusionForItem) return;

    setIsAddingItem(true);
    try {
      const response = await addBOQToInclusion(
        id,
        selectedInclusionForItem.id,
        itemData
      );

      if (response.success) {
        toast.success(
          `Successfully added item to ${selectedInclusionForItem.name}`
        );
        loadRoomData(); // Reload data
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    } finally {
      setIsAddingItem(false);
    }
  };

  // Image upload functions
  const handleImageUpload = async (files, tags) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append("project_id", roomId);
      formData.append("type", "room");
      formData.append("tags", tags);

      // Add up to 10 files
      const filesToUpload = Array.from(files).slice(0, 10);
      filesToUpload.forEach((file, index) => {
        formData.append(`file_${index + 1}`, file);
      });

      const response = await postFormData("api/project/files", formData);

      if (response.success) {
        toast.success(`Successfully uploaded ${filesToUpload.length} image(s)`);
        // Reload images for the specific tag
        const newImages = await fetchImages("room", tags);

        // Map tags to keys
        let key;
        switch (tags) {
          case "INSPIRATION":
            key = "inspirations";
            break;
          case "FLOORPLAN_AS_BUILT":
            key = "floorplans_as_built";
            break;
          case "FLOORPLAN_TARGET":
            key = "floorplans_target";
            break;
          case "3D_RENDER":
            key = "renders_3d";
            break;
          case "ELEVATION":
            key = "elevations";
            break;
          case "PICTURE":
            key = "pictures";
            break;
          default:
            key = tags.toLowerCase().replace("_", "_");
        }

        setImages((prev) => ({
          ...prev,
          [key]: newImages,
        }));
      } else {
        toast.error("Failed to upload images");
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleFileInputChange = (event, tags) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files, tags);
    }
    // Reset input
    event.target.value = "";
  };

  const handleDeleteImage = async (imageId, tags) => {
    try {
      const response = await getApiData(
        `api/project/delete-files/${imageId}`,
        "POST",
        {}
      );
      if (response.success) {
        toast.success("Image deleted successfully");
        // Reload images for the specific tag
        const newImages = await fetchImages("room", tags);

        // Map tags to keys
        let key;
        switch (tags) {
          case "INSPIRATION":
            key = "inspirations";
            break;
          case "FLOORPLAN_AS_BUILT":
            key = "floorplans_as_built";
            break;
          case "FLOORPLAN_TARGET":
            key = "floorplans_target";
            break;
          case "3D_RENDER":
            key = "renders_3d";
            break;
          case "ELEVATION":
            key = "elevations";
            break;
          case "PICTURE":
            key = "pictures";
            break;
          default:
            key = tags.toLowerCase().replace("_", "_");
        }

        setImages((prev) => ({
          ...prev,
          [key]: newImages,
        }));
      } else {
        toast.error("Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  // Style modal handlers
  const handleOpenStyleModal = () => {
    if (currentStyle) setSelectedStyleId(currentStyle.id);
    setIsStyleModalOpen(true);
  };

  const handleCloseStyleModal = () => {
    setIsStyleModalOpen(false);
    if (currentStyle) setSelectedStyleId(currentStyle.id);
  };

  const handleUpdateStyle = async () => {
    if (!selectedStyleId) {
      toast.error("Please select a style");
      return;
    }
    setIsUpdatingStyle(true);
    try {
      const response = await updateRoom(id, {
        rooms: [
          {
            room_id: parseInt(roomId),
            style_id: selectedStyleId,
          },
        ],
      });
      if (response?.success) {
        toast.success("Style updated successfully");
        const updated = availableStyles.find((s) => s.id === selectedStyleId);
        if (updated) {
          setCurrentStyle(updated);
          setDisplayStyleName(updated.name);
        }
        setIsStyleModalOpen(false);
      } else {
        toast.error(response?.message || "Failed to update style");
      }
    } catch (error) {
      console.error("Error updating style:", error);
      toast.error("Failed to update style. Please try again.");
    } finally {
      setIsUpdatingStyle(false);
    }
  };

  // Palette modal handlers
  const handleOpenPaletteModal = () => {
    if (currentPalette) setSelectedPaletteId(currentPalette.id);
    setIsPaletteModalOpen(true);
  };

  const handleClosePaletteModal = () => {
    setIsPaletteModalOpen(false);
    if (currentPalette) setSelectedPaletteId(currentPalette.id);
  };

  const handleUpdatePalette = async () => {
    if (!selectedPaletteId) {
      toast.error("Please select a palette");
      return;
    }
    setIsUpdatingPalette(true);
    try {
      const response = await updateRoom(id, {
        rooms: [
          {
            room_id: parseInt(roomId),
            color_palette_id: selectedPaletteId,
          },
        ],
      });
      if (response?.success) {
        toast.success("Palette updated successfully");
        const updated = availablePalettes.find((p) => p.id === selectedPaletteId);
        if (updated) {
          setCurrentPalette(updated);
          setDisplayPaletteName(updated.name);
        }
        setIsPaletteModalOpen(false);
      } else {
        toast.error(response?.message || "Failed to update palette");
      }
    } catch (error) {
      console.error("Error updating palette:", error);
      toast.error("Failed to update palette. Please try again.");
    } finally {
      setIsUpdatingPalette(false);
    }
  };

  const renderRoomBOQ = () => {
    return (
      <div style={{marginTop: "1.2rem"}}>
      <RoomScope
        projectId={id}
        roomId={roomId}
        roomData={currentRoomData}
        projectDetails={details}
        isMobile={isMobile}
        isTablet={isTablet}
      />
      </div>
    );
  };

  // Image Gallery Component
  const ImageGallery = ({
    images,
    tags,
    title,
    description,
  }) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 500, color: "#111827", letterSpacing: 0 }}
          >
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {description}
          </Typography>
        </Box>

        {/* Images Grid */}
        {images.length > 0 ? (
          <Grid container spacing={2}>
            {images.map((image) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={image.id}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "100%", // 1:1 aspect ratio
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <Box
                    component="img"
                    src={image.path}
                    alt={title}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  {!image.path && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40, color: "#949DAA" }} />
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              border: "1px dashed #e2e8f0",
              borderRadius: "8px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#fafafa",
            }}
          >
            <ImageIcon sx={{ fontSize: 32, color: "#949DAA", mb: 1 }} />
            <Typography variant="body2" sx={{ color: "#666" }}>
              No images available
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 1, fontWeight: 500, color: "#111827", letterSpacing: 0 }}
            >
              Selected Style & Color Palette
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, letterSpacing: 0 }}
            >
              View and edit your room's selected style and color palette
            </Typography>

            <Grid container spacing={3}>
              {/* Current Style */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 500, color: "#111827" }}
                >
                  Current Style
                </Typography>
                <Card
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    p: 2,
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "128px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "8px",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {currentStyle?.image_url ? (
                      <Box
                        component="img"
                        src={currentStyle.image_url}
                        alt={displayStyleName || "Style"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 32, color: "#949DAA" }} />
                    )}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color: "#111827",
                      mb: 0.5,
                      fontSize: "14px",
                    }}
                  >
                    {displayStyleName || "Not selected"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", fontSize: "12px" }}
                  >
                    {currentStyle?.description ||
                      "Clean lines, neutral colors, and uncluttered spaces"}
                  </Typography>
                  {/* <Button
                    onClick={handleOpenStyleModal}
                    sx={{
                      mt: 2,
                      textTransform: "none",
                      color: "#4d6bf6",
                      fontSize: "12px",
                      boxShadow: "none",
                      p: 0,
                      minWidth: "auto",
                    }}
                  >
                    Change Style
                  </Button> */}
                </Card>
              </Grid>

              {/* Color Palette */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 500, color: "#111827" }}
                >
                  Color Palette
                </Typography>
                <Card
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    p: 2,
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "128px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "8px",
                      mb: 2,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {currentPalette?.image_url ? (
                      <Box
                        component="img"
                        src={currentPalette.image_url}
                        alt={displayPaletteName || "Palette"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : paletteColors[displayPaletteName] ? (
                      <ColorPalettePreview
                        colors={paletteColors[displayPaletteName]}
                      />
                    ) : currentPalette?.name &&
                      paletteColors[currentPalette.name] ? (
                      <ColorPalettePreview
                        colors={paletteColors[currentPalette.name]}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 32, color: "#949DAA" }} />
                    )}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color: "#111827",
                      mb: 0.5,
                      fontSize: "14px",
                    }}
                  >
                    {displayPaletteName || "Not selected"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", fontSize: "12px" }}
                  >
                    {currentPalette?.description ||
                      "Neutral and calming color scheme"}
                  </Typography>
                  {/* <Button
                    onClick={handleOpenPaletteModal}
                    sx={{
                      mt: 2,
                      textTransform: "none",
                      color: "#4d6bf6",
                      fontSize: "12px",
                      boxShadow: "none",
                      p: 0,
                      minWidth: "auto",
                    }}
                  >
                    Change Palette
                  </Button> */}
                </Card>
              </Grid>
            </Grid>

            {/* Pictures Section */}
            <Box sx={{ mt: 4 }}>
              <ImageGallery
                images={images.pictures}
                tags="PICTURE"
                title="Pictures"
                description="Upload pictures for this room"
              />
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* As-Built Floorplans */}
              <ImageGallery
                images={images.floorplans_as_built}
                tags="FLOORPLAN_AS_BUILT"
                title="As-Built Floorplans"
                description="Upload current state drawings of the room"
              />

              {/* Target Floorplans */}
              <ImageGallery
                images={images.floorplans_target}
                tags="FLOORPLAN_TARGET"
                title="Target Floorplans"
                description="Upload planned design drawings of the room"
              />
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <ImageGallery
              images={images.inspirations}
              tags="INSPIRATION"
              title="Inspiration Images"
              description="Upload inspiration images and reference photos for this room"
            />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* 3D Renders */}
              <ImageGallery
                images={images.renders_3d}
                tags="3D_RENDER"
                title="3D Renders"
                description="Upload 3D render images of the room"
              />

              {/* Elevations */}
              <ImageGallery
                images={images.elevations}
                tags="ELEVATION"
                title="Elevations"
                description="Upload elevation drawings of the room"
              />
            </Box>
          </Box>
        );
      case 4:
        return renderRoomBOQ();
      case 5:
        return (
          <RoomActivities
            roomId={roomId}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={roomStyles.loadingContainer}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={roomStyles.loadingText}>
          Loading room details...
        </Typography>
      </Box>
    );
  }

  if (!currentRoomData || !id || !roomId) {
    return (
      <Box sx={roomStyles.container}>
        <Box sx={roomStyles.header}>
          <Box sx={roomStyles.headerLeft}>
            <IconButton onClick={handleBack} sx={roomStyles.backButton}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={roomStyles.title}>
                Room Details
              </Typography>
              <Typography variant="body2" sx={roomStyles.subtitle}>
                No room selected
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Please select a room to view its details.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Debug information
  console.log("RoomDetails render - currentRoomData:", currentRoomData);
  console.log("RoomDetails render - inclusions:", inclusions);
  console.log("RoomDetails render - loading:", loading);
  console.log("RoomDetails render - error:", error);

  return (
    <div
      style={{
        ...(passedStyles?.container || {}),
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <header
        style={{ ...(passedStyles?.header || {}), flexShrink: 0 }}
      >
        <div style={passedStyles?.headerContent || {}}>
          <LeftArrowIcon
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer" }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div
              style={{
                ...passedStyles?.idText,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              {currentRoomData.room_name || "Room"} - Room Details
            </div>
            <div style={{ color: "#949daa", fontSize: "14px" }}>
              Detailed room information and BOQ breakdown
            </div>
          </div>
        </div>
      </header>

      <div
        style={{
          padding: "2vh 2vw 0",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile={false}
          sx={{
            flexShrink: 0,
            "& .MuiTabs-indicator": {
              background: "none",
            },
            "& .MuiTabs-scrollButtons": {
              display: "none",
            },
            "& .MuiTabs-scroller": {
              overflowX: "auto !important",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            },
          }}
        >
          <StyledTab label="Style & Palette" />
          <StyledTab label="Floorplans & Drawings" />
          <StyledTab label="Inspirations" />
          <StyledTab label="3D Renders & Elevations" />
          <StyledTab label="Room BOQ" />
          <StyledTab label="Activities" />
        </Tabs>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            paddingBottom: "2vh",
          }}
        >
          {activeTab === 4 || activeTab === 5 ? (
            renderTabContent()
          ) : (
            <Box sx={roomStyles.tabContent}>{renderTabContent()}</Box>
          )}
        </div>
      </div>

      {/* Style Selection Modal */}
      <Dialog
        open={isStyleModalOpen}
        onClose={handleCloseStyleModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: "#0d0d0e" }}>
            Change Style
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Choose the style that best represents the feeling you want to create in this space.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingStyles ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : availableStyles.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#666" }}>
              No styles available
            </Box>
          ) : (
            <Grid container spacing={2}>
              {availableStyles.map((style) => {
                const isSelected = selectedStyleId === style.id;
                return (
                  <Grid item xs={12} sm={6} md={4} key={style.id}>
                    <Box
                      onClick={() => setSelectedStyleId(style.id)}
                      sx={{
                        position: "relative",
                        border: isSelected
                          ? "2px solid #4d6bf6"
                          : "2px solid #e5e7eb",
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#eff6ff" : "#fff",
                        "&:hover": {
                          borderColor: isSelected ? "#4d6bf6" : "#d1d5db",
                        },
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: "#4d6bf6",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: "100%",
                          height: 112,
                          backgroundColor: "#e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {style.image_url ? (
                          <Box
                            component="img"
                            src={style.image_url}
                            alt={style.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <ImageIcon sx={{ fontSize: 28, color: "#949daa" }} />
                        )}
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#111827" }}
                        >
                          {style.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={handleCloseStyleModal}
            disabled={isUpdatingStyle}
            sx={{ textTransform: "none", color: "#0d0d0e" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStyle}
            disabled={!selectedStyleId || isUpdatingStyle}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#0d0d0d",
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            {isUpdatingStyle ? "Updating..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Palette Selection Modal */}
      <Dialog
        open={isPaletteModalOpen}
        onClose={handleClosePaletteModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: "#0d0d0e" }}>
            Change Color Palette
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Select the color palette that complements your chosen style and personal preference.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingPalettes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : availablePalettes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#666" }}>
              No palettes available
            </Box>
          ) : (
            <Grid container spacing={2}>
              {availablePalettes.map((palette) => {
                const isSelected = selectedPaletteId === palette.id;
                return (
                  <Grid item xs={12} sm={6} md={4} key={palette.id}>
                    <Box
                      onClick={() => setSelectedPaletteId(palette.id)}
                      sx={{
                        position: "relative",
                        border: isSelected
                          ? "2px solid #4d6bf6"
                          : "2px solid #e5e7eb",
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#eff6ff" : "#fff",
                        "&:hover": {
                          borderColor: isSelected ? "#4d6bf6" : "#d1d5db",
                        },
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: "#4d6bf6",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: "100%",
                          height: 112,
                          backgroundColor: "#e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {palette.image_url ? (
                          <Box
                            component="img"
                            src={palette.image_url}
                            alt={palette.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : paletteColors[palette.name] ? (
                          <ColorPalettePreview
                            colors={paletteColors[palette.name]}
                          />
                        ) : (
                          <ImageIcon sx={{ fontSize: 28, color: "#949daa" }} />
                        )}
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#111827" }}
                        >
                          {palette.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={handleClosePaletteModal}
            disabled={isUpdatingPalette}
            sx={{ textTransform: "none", color: "#0d0d0e" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePalette}
            disabled={!selectedPaletteId || isUpdatingPalette}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#0d0d0d",
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            {isUpdatingPalette ? "Updating..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default RoomDetails;
